import { DEFAULT_LOCALE } from "../i18n/locales.js";
import { episodeDates } from "../data/episodeDates.js";
import {
  absoluteUrl,
  SITE_DESCRIPTION,
  SITE_FOUNDER,
  SITE_NAME,
  SITE_SAME_AS,
  SITE_URL
} from "./metaDefaults.js";
import { seoSource } from "./routeRecords.js";

const { articles, courses, downloads, episodes, mediaSeasons, programs, seasons, text } = seoSource;

function graphId(path, fragment) {
  return `${absoluteUrl(path)}#${fragment}`;
}

function isVideoFileUrl(url) {
  return typeof url === "string" && /\.(mp4|m4v|mov|webm)(?:[?#].*)?$/i.test(url);
}

function uploadDateFor(episode) {
  const date = episode.date || episodeDates[episode.id];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return undefined;
  return `${date}T00:00:00+00:00`;
}

function videoContentUrlFor(episode) {
  if (!isVideoFileUrl(episode.videoUrl)) return undefined;
  return absoluteUrl(episode.videoUrl);
}

function videoEmbedUrlFor(episode) {
  if (!episode.videoUrl || isVideoFileUrl(episode.videoUrl)) return undefined;
  return absoluteUrl(episode.videoUrl);
}

function baseGraph(record, meta) {
  return [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      description: SITE_DESCRIPTION,
      logo: {
        "@type": "ImageObject",
        url: absoluteUrl("/assets/images/TGW_logo_def.png")
      },
      founder: { "@id": `${SITE_URL}/#founder` },
      sameAs: SITE_SAME_AS
    },
    {
      "@type": "Person",
      "@id": `${SITE_URL}/#founder`,
      name: SITE_FOUNDER.name,
      url: SITE_FOUNDER.url,
      description: SITE_FOUNDER.description,
      worksFor: { "@id": `${SITE_URL}/#organization` }
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: SITE_NAME,
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: record.lang
    },
    {
      "@type": "WebPage",
      "@id": graphId(record.path, "webpage"),
      url: meta.canonical,
      name: meta.title,
      description: meta.description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      inLanguage: record.lang,
      mainEntity: record.entity?.type === "article" ? { "@id": graphId(record.path, "article") } : undefined
    },
    buildBreadcrumb(record)
  ];
}

function buildBreadcrumb(record) {
  const parts = record.path.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: SITE_NAME,
      item: absoluteUrl(`/${record.lang}/`)
    }
  ];

  if (parts[1]) {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: record.sectionLabel,
      item: absoluteUrl(`/${record.lang}/${parts[1]}`)
    });
  }

  if (parts.length > 2) {
    items.push({
      "@type": "ListItem",
      position: 3,
      name: record.title,
      item: absoluteUrl(record.path)
    });
  }

  return {
    "@type": "BreadcrumbList",
    "@id": graphId(record.path, "breadcrumb"),
    itemListElement: items
  };
}

function entityNode(record, meta) {
  if (!record.entity) {
    if (record.sectionKey === "about") {
      return {
        "@type": "AboutPage",
        "@id": graphId(record.path, "about"),
        name: meta.title,
        description: meta.description,
        url: meta.canonical
      };
    }
    return null;
  }

  const { type, id } = record.entity;

  if (type === "article") {
    const article = articles.find((item) => item.id === id);
    return {
      "@type": "Article",
      "@id": graphId(record.path, "article"),
      headline: record.title,
      description: meta.description,
      datePublished: meta.datePublished || article.date,
      dateModified: meta.dateModified || article.date,
      author: { "@type": "Person", name: meta.author || article.author },
      publisher: { "@id": `${SITE_URL}/#organization` },
      image: meta.image,
      url: meta.canonical,
      mainEntityOfPage: meta.canonical,
      inLanguage: record.lang,
      keywords: meta.tags,
      articleSection: meta.articleSection
    };
  }

  if (type === "course") {
    const course = courses.find((item) => item.id === id);
    return {
      "@type": "Course",
      "@id": graphId(record.path, "course"),
      name: text(course.title, record.lang),
      description: meta.description,
      educationalLevel: course.level,
      inLanguage: course.language,
      provider: { "@id": `${SITE_URL}/#organization` },
      url: meta.canonical
    };
  }

  if (type === "episode") {
    const episode = episodes.find((item) => item.id === id);
    const nodeType = episode.videoUrl ? "VideoObject" : "CreativeWork";
    return {
      "@type": nodeType,
      "@id": graphId(record.path, "episode"),
      name: text(episode.title, record.lang),
      description: meta.description,
      thumbnailUrl: episode.image ? absoluteUrl(episode.image) : undefined,
      uploadDate: nodeType === "VideoObject" ? uploadDateFor(episode) : undefined,
      embedUrl: nodeType === "VideoObject" ? videoEmbedUrlFor(episode) : undefined,
      contentUrl: nodeType === "VideoObject" ? videoContentUrlFor(episode) : episode.audioUrl ? absoluteUrl(episode.audioUrl) : undefined,
      inLanguage: episode.language,
      url: meta.canonical
    };
  }

  if (type === "download") {
    const download = downloads.find((item) => item.id === id);
    return {
      "@type": "SoftwareApplication",
      "@id": graphId(record.path, "software"),
      name: text(download.title, record.lang),
      description: meta.description,
      applicationCategory: download.category,
      operatingSystem: download.builds.length ? download.builds.map((build) => build.platform).join(", ") : "Not released",
      softwareVersion: download.version || undefined,
      offers: download.status === "coming-soon" ? undefined : { "@type": "Offer", price: "0", priceCurrency: "USD" },
      url: meta.canonical
    };
  }

  if (type === "program" || type === "season" || type === "mediaSeason") {
    const item =
      type === "program"
        ? programs.find((entry) => entry.id === id)
        : type === "season"
          ? seasons.find((entry) => entry.id === id)
          : mediaSeasons.find((entry) => entry.id === id);
    return {
      "@type": "CreativeWorkSeries",
      "@id": graphId(record.path, type),
      name: text(item.title, record.lang),
      description: meta.description,
      inLanguage: DEFAULT_LOCALE,
      url: meta.canonical
    };
  }

  return null;
}

function compactJsonLd(value) {
  if (Array.isArray(value)) {
    return value.map(compactJsonLd).filter((entry) => entry !== undefined);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, entry]) => [key, compactJsonLd(entry)])
        .filter(([, entry]) => entry !== undefined && entry !== null && entry !== "")
    );
  }
  return value;
}

export function buildJsonLd(record, meta) {
  const graph = baseGraph(record, meta);
  const node = entityNode(record, meta);
  if (node) graph.push(node);

  return compactJsonLd({
    "@context": "https://schema.org",
    "@graph": graph
  });
}
